import * as R from 'ramda';
import React from 'react';
import { debounce } from 'throttle-debounce';
import Zousan from 'zousan';

import { Icon } from '@ui/icons/Icon.tsx';
import { throttle, runOnce, changeTest, changeTestForArray } from '@utils/funcs.ts';
import { distance } from '@utils/geodesy.ts';
import { getLocation } from '@utils/location.ts';
import { adjustBoundsToPois } from '@utils/poi.ts';
import { ROUTE_START_MARKER_ID, getRouteEndMarkerId } from '@utils/routeMarkerIds.ts';
import { StateObjMultiple } from '@utils/stateObj.ts';

import {
  ANIMATION_DELAY,
  ANIMATION_TIME,
  ANNOUNCE_DEBOUNCE_TIME,
  DEBOUNCE_TIME,
  DIRECTIONS_RESULT_CONTROLS_ID,
  DIRECTIONS_RESULT_VIEW_ID_DESKTOP,
  DIRECTIONS_RESULT_VIEW_ID_MOBILE,
  DIRECTIONS_SEARCH_CONTROLS_ID,
  DIRECTIONS_SEARCH_VIEW_ID,
  DISPLAY_UNITS_METERS,
  DISPLAY_UNITS_FEET,
  FROM_SEARCH_FIELD_ID,
  LANE_PICKER_VIEW_ID,
  MAX_SEARCH_INPUT_LENGTH,
  MAX_UI_RESULTS_LENGTH,
  RouteOptionsClassification,
  RouteType,
  TO_SEARCH_FIELD_ID,
} from './constants.ts';
import { createDirectionsMetrics } from './directionsMetrics.ts';
import DirectionsResultControls from './DirectionsResultControls/DirectionsResultControls.tsx';
import DirectionsSearchControls from './DirectionsSearchControls/DirectionsSearchControls.tsx';
import DirectionsSearchView from './DirectionsSearchView/DirectionsSearchView.tsx';
import { getNextEmptySearchFieldIndex } from './inputs.ts';
import {
  getPoisByTerm,
  getPoisWithTransitTimes,
  allEndPointsDefined,
  endPointDefined,
  definedEndpointCount,
} from './poiListingUtils.ts';
import showDirectionsFromToCreate from './showDirectionsFromTo.tsx';
import { GhostMarker } from './styles.ts';
import { getDefaultState, getSelectedSecurityLanes, setDefaultRouteAccessibilityChoice } from './widgetState.ts';

export const getEntitiesReferencedBySearchInputs = ({ searchInputs = [] }) =>
  R.map(searchInput => {
    if (endPointDefined(searchInput)) {
      if (searchInput.location) {
        // TODO better way to test for my location vs. general location
        return 'myLocation';
      } else {
        return `${poiOrLocation(searchInput)}`; // TODO Just need to call poi here
      }
    } else {
      return null;
    }
  }, searchInputs);

// takes search input
export const poiOrLocation = R.either(R.path(['chosenPoi', 'poiId']), R.prop('location'));
const allSearchEndpoints = searchInputs => R.map(poiOrLocation, searchInputs);

const _create = widgetState =>
  function create(app, config) {
    let logoUrl = null;

    const isLayerShowing = async id =>
      app.bus.send('layers/isShowing', { id })?.then(res => res.length > 0 && res[0]) ?? false; // layerManager is not loaded for SDK

    app.bus.on('directions/getPreferredUnits', () => widgetState.getState().preferredUnits);
    app.bus.on('directions/getActiveRoute', () => {
      const { steps, endpoints, searchInputs, distances, times, preferredUnits } = widgetState.getState();
      const hasRoute = steps.some(leg => leg.length > 0) && endpoints.length >= 2;
      if (!hasRoute) {
        return null;
      }
      return {
        steps,
        endpoints,
        searchInputs,
        totalDistance: R.sum(distances || []),
        totalTime: R.sum(times || []),
        preferredUnits,
      };
    });

    const init = async () => {
      logoUrl = await app.bus.get('theme/getVenueLogo');

      const handleWaypointsInput = (waypoints, minimumLen = 2) => {
        // if we enable bluedot then allow a min len of 1
        if (Array.isArray(waypoints) && waypoints.length >= minimumLen) {
          return waypoints;
        }
        try {
          const parsedWaypoints = JSON.parse(waypoints);
          if (!Array.isArray(parsedWaypoints)) {
            throw new Error('Deep linked waypoints not an array');
          }
          if (parsedWaypoints.length < 2) {
            throw new Error('Did not provide multiple waypoints');
          }
          return parsedWaypoints;
        } catch {
          return [];
        }
      };

      const dlp = config.deepLinkProps;
      if (dlp) {
        app.bus.on(
          'map/mapReadyToShow',
          runOnce(() => {
            const requiresAccessibility = dlp.accessible === true || dlp.accessible === 'true';
            if (dlp.waypoints) {
              const parsedWaypoints = handleWaypointsInput(dlp.waypoints).slice(0, 7);
              if (dlp.showNav && dlp.showNav === 'true') {
                app.bus.send('navigation/searchDirectionsToId', {
                  waypoints: parsedWaypoints,
                  requiresAccessibility,
                  referrer: 'prog',
                });
              } else {
                app.bus.send('navigation/showDirectionsFromTo', {
                  endpointIds: parsedWaypoints,
                  requiresAccessibility,
                  referrer: 'prog',
                });
              }
            } else if (dlp.navFrom || dlp.navTo) {
              if (dlp.navFrom && dlp.navTo) {
                app.bus.send('navigation/showDirectionsFromTo', {
                  endpointIds: [dlp.navFrom, dlp.navTo],
                  requiresAccessibility,
                  referrer: 'prog',
                });
              } else {
                if (dlp.navFrom) {
                  app.bus.send('navigation/searchDirectionsFromId', {
                    poiId: dlp.navFrom,
                    requiresAccessibility,
                    referrer: 'prog',
                  });
                }
                if (dlp.navTo) {
                  app.bus.send('navigation/searchDirectionsToId', {
                    poiId: dlp.navTo,
                    requiresAccessibility,
                    referrer: 'prog',
                  });
                }
              }
            } else if (dlp.showNav) {
              if (dlp.showNav === 'true') {
                app.bus.send('navigation/searchDirectionsToId', {
                  poiId: dlp.poiId,
                  requiresAccessibility,
                  referrer: 'prog',
                });
              } else if (dlp.poiId) {
                app.bus.send('navigation/showDirectionsFromTo', {
                  endpointIds: [dlp.showNav, dlp.poiId],
                  requiresAccessibility,
                  referrer: 'prog',
                });
              } else {
                app.bus.send('navigation/searchDirectionsFromId', {
                  poiId: dlp.showNav,
                  referrer: 'prog',
                });
              }
            }
          }),
        );
      }

      const T = app.gt();
      const displayUnitLabels = {
        [DISPLAY_UNITS_METERS]: T('getDirectionsFromTo:Display Distance in Feet'),
        [DISPLAY_UNITS_FEET]: T('getDirectionsFromTo:Display Distance in Meters'),
      };

      app.bus.send('userMenu/addItem', {
        label: displayUnitLabels[preferredUnits],
        iconId: 'measure',
        id: 'displayUnits',
        onClick: async () => {
          const { preferredUnits } = widgetState.getState();
          const newPreferredUnits = preferredUnits === DISPLAY_UNITS_METERS ? DISPLAY_UNITS_FEET : DISPLAY_UNITS_METERS;
          app.bus.send('appInsights/log', {
            name: 'getDirectionsFromTo/toggleDisplayUnits',
            properties: { newPreferredUnits },
          }); // this was logging the old units?
          widgetState.update({
            preferredUnits: newPreferredUnits,
          });
          const isDesktop = app.env.isDesktop();
          const isNavigationShowing = await isLayerShowing(
            isDesktop ? DIRECTIONS_RESULT_VIEW_ID_DESKTOP : DIRECTIONS_RESULT_VIEW_ID_MOBILE,
          );
          const { endpointIds, securityLanes, immigrationLanes, isRoutePending, hasLoaded } = widgetState.getState();
          if (isNavigationShowing && endpointIds.length >= 2 && !(isRoutePending && hasLoaded)) {
            // On units change click, re compute and display route with new units
            const selectedSecurityLanes = getSelectedSecurityLanes(securityLanes, immigrationLanes);
            await app.bus.send('navigation/showDirectionsFromTo', {
              endpointIds,
              selectedSecurityLanes,
            });
          }

          return {
            label: displayUnitLabels[newPreferredUnits],
          };
        },
      });

      app.bus.send('ariaAnnouncer/registerNamespace', {
        announcementKey: 'directionsStep',
      });

      app.bus.send('ariaAnnouncer/registerNamespace', {
        announcementKey: 'directionsSearchResults',
      });

      app.bus.send('ariaAnnouncer/registerNamespace', {
        announcementKey: 'directionsRouteUnavailable',
      });
    };

    // default to showing "meters" as distance unit, but allow "feet" to be configured to be default as well
    const preferredUnits = ['feet', 'yards'].includes(config.displayUnits?.toLowerCase())
      ? DISPLAY_UNITS_FEET
      : DISPLAY_UNITS_METERS;

    // set modal visibility on widget state
    isLayerShowing('modalWindow').then(() => {
      widgetState.update({ modalIsShowing: true });
    });

    const { Loading } = app.themePack;
    // Full Route Preview feature flag (see plugins/online/getDirectionsFromTo/config-schema.ts).
    // Defaults to disabled; when true, downstream UI can show a route preview step before
    // navigation starts. Stored on widgetState so it's available to child components/hooks
    // without needing to thread `config` through every layer.
    const fullRoutePreviewEnabled = config.fullRoutePreviewEnabled === true;
    widgetState.update({
      ...getDefaultState(),
      preferredUnits,
      fullRoutePreviewEnabled,
    });

    let levelPopupTimeout = null;

    createDirectionsMetrics(app.bus, {
      directionsHeaderIds: [DIRECTIONS_SEARCH_CONTROLS_ID, DIRECTIONS_RESULT_CONTROLS_ID],
      getContext: () => {
        const { referrer, searchInputs, routeAccessibiltyChoice, routeTypes, distances, times, steps } =
          widgetState.getState();
        return {
          referrer,
          stopCount: definedEndpointCount(searchInputs),
          stops: searchInputs.map(input => input?.chosenPoi?.poiId ?? null),
          startedFromLocation: Boolean(searchInputs[FROM_SEARCH_FIELD_ID]?.location),
          accessibleRoute:
            routeTypes === RouteOptionsClassification.DEFAULT_IS_ACCESSIBLE ||
            (routeAccessibiltyChoice === RouteType.ACCESSIBLE &&
              routeTypes !== RouteOptionsClassification.NO_ACCESSIBLE_ROUTES),
          routeDistanceMeters: Math.round(R.sum(distances)),
          routeEstimatedMs: Math.round(R.sum(times) * 60_000),
          stepsTotal: R.sum(steps.map(leg => leg.length)),
          deviceType: app.env.isMobile() ? 'mobile' : 'desktop',
        };
      },
    });

    const {
      displayDirections,
      animateToPoint,
      toggleIsNavigating,
      getInputNewData,
      displayRoutes,
      ...showDirectionsFromToTestMethods
    } = showDirectionsFromToCreate(app, config, widgetState);

    app.bus.send('layers/register', {
      id: DIRECTIONS_SEARCH_VIEW_ID,
      widget: () => (
        <DirectionsSearchView
          widgetState={widgetState}
          isDesktop={app.env.isDesktop}
          Loading={Loading}
          handleChooseMapLocationClicked={handleChooseMapLocationClicked}
          handlePoiClicked={setInputChosenPoi}
          handleEscapeKey={exitGetDirections}
          T={app.gt()}
        />
      ),
      layoutId: 'content',
      layoutName: 'fullscreen',
    });

    const handleChooseMapLocationClicked = () => hideSearchView();

    app.bus.send('layers/register', {
      id: DIRECTIONS_SEARCH_CONTROLS_ID,
      widget: () => (
        <DirectionsSearchControls
          handleInputSelect={setCurrentInputIndex}
          onInputClearButtonClick={handleSearchControlsInputClearButtonClick}
          handleSwitchInputsButtonClicked={switchDirections}
          handleKeyPress={handleKeyPress}
          widgetState={widgetState}
          logoUrl={logoUrl}
          logoLinkUrl={app.config.logoLinkUrl}
          clientName={app.config.name}
          isDesktop={() => app.env.isDesktop()}
          handleInputValueChange={handleInputValueChange}
          onBackButtonClicked={exitGetDirections}
          handleSearchInputsChange={handleSearchInputsChange}
          onSwitchRouteTypeButtonClicked={switchRouteType}
          T={app.gt()}
        />
      ),
      layoutId: 'headerOnline',
    });

    function handleSearchInputsChange(newSearchInputs) {
      const { endpointIds } = widgetState.getState();
      const newEndpointIds = allSearchEndpoints(newSearchInputs);

      // if the endpoints changed, e.g. we removed a stop, force a re-route
      if (endpointIds.length !== newEndpointIds.length && allEndPointsDefined(newSearchInputs)) {
        app.bus.send('navigation/stopsChanged', { endpointIds: newEndpointIds, userInitiated: true });
        app.bus.send('navigation/showDirectionsFromTo', {
          endpointIds: newEndpointIds,
        });
      } else {
        widgetState.update({ searchInputs: [...newSearchInputs] });
      }
    }

    function handleSearchControlsInputClearButtonClick(index = null) {
      resetUnavailableRouteInfo();

      resetInputData({ index });
    }

    function handleKeyPress(event, term, index) {
      if (event.key === 'Enter') {
        widgetState.update({ currentInputIndex: index }); // set the currentIndex when we click enter in the input field
        if (term.length >= 3) {
          performSearch(term, true);
        }
      }
    }

    function resetUnavailableRouteInfo() {
      const { isRouteUnavailable } = widgetState.getState();
      if (isRouteUnavailable) {
        widgetState.update({ isRouteUnavailable: false });
      }

      // Clear the dangling screen-reader announcement whenever we leave the
      // unavailable state.
      app.bus.send('ariaAnnouncer/announce', {
        announcementKey: 'directionsRouteUnavailable',
        announcementText: '',
      });
    }

    app.bus.send('layers/register', {
      id: DIRECTIONS_RESULT_CONTROLS_ID,
      widget: () => (
        <DirectionsResultControls
          onBackFromNavigationClicked={toggleIsNavigating}
          onBackButtonClicked={exitGetDirections}
          onBackToSearchButtonClicked={onBackToSearchButtonClicked}
          onSwitchDirectionsButtonClicked={switchDirections}
          onSwitchRouteTypeButtonClicked={switchRouteType}
          onInputClearButtonClick={handleInputCloseButtonClick}
          handleChange={handleChange}
          widgetState={widgetState}
          logoUrl={logoUrl}
          logoLinkUrl={app.config.logoLinkUrl}
          clientName={app.config.name}
          theme={app.config.theme}
          isBackButtonDisabled={config.isBackButtonDisabled}
          isDesktop={() => app.env.isDesktop()}
          mobileHighZoomBreakpoint={app.config.mobileHighZoomBreakpoint}
          handleInputSelect={handleInputSelect}
          preferredUnits={DISPLAY_UNITS_METERS}
          handleAddStopSelect={handleAddStopSelect}
          T={app.gt()}
          handleSearchInputsChange={handleSearchInputsChange}
        />
      ),
      layoutId: 'headerOnline',
    });

    const switchDirections = () => {
      const { searchInputs, currentInputIndex } = widgetState.getState();
      const updatedSearchInputs = [{ ...searchInputs[TO_SEARCH_FIELD_ID] }, { ...searchInputs[FROM_SEARCH_FIELD_ID] }];
      const nextFocusedInput = getNextEmptySearchFieldIndex(searchInputs, currentInputIndex); // switch between focused inputs just returns the other field Id right now.....

      const { endpoints, endpointIds } = widgetState.getState();
      widgetState.update({
        endpointIds: endpointIds.reverse(),
        endpoints: endpoints.reverse(),
        searchInputs: updatedSearchInputs,
        currentInputIndex: nextFocusedInput,
      });
      app.bus.send('navigation/stopsChanged', {
        endpointIds: allSearchEndpoints(updatedSearchInputs),
        userInitiated: true,
      });
    };

    const switchRouteType = async routeType => {
      const previous = widgetState.getState().routeAccessibiltyChoice;
      widgetState.update({ routeAccessibiltyChoice: routeType });
      app.bus.send('navigation/routeTypeChanged', { previous, current: routeType, userInitiated: true });
      const { endpoints, endpointIds, searchInputs, isRoutePending, hasLoaded, selectedSecurityLanes } =
        widgetState.getState();
      setDefaultRouteAccessibilityChoice(routeType);
      if (allEndPointsDefined(searchInputs)) {
        if (isRoutePending) {
          return app.bus.send('navigation/showDirectionsFromTo', {
            endpointIds: hasLoaded ? allSearchEndpoints(searchInputs) : endpointIds,
            selectedSecurityLanes,
          });
        }
        return displayDirections({
          endpoints,
          wasRoutePreviouslyDisplayed: true,
        });
      }
    };

    const handleChange = term => {
      navigateBack();

      app.bus.send('online/handleSearchInputChange', { term });
    };
    const handleInputSelect = id => {
      widgetState.update({ currentInputIndex: id });
    };

    const handleAddStopSelect = id => {
      navigateBack();

      app.bus.send('online/setCurrentSearchInput', { id });
    };

    const handleInputCloseButtonClick = id => {
      navigateBack();

      app.bus.send('online/resetSearchInputData', { index: id });
    };

    const navigateBack = () => {
      app.bus.send('navigation/editStops');
      app.bus.send('layers/hideMultiple', [
        DIRECTIONS_RESULT_CONTROLS_ID,
        DIRECTIONS_RESULT_VIEW_ID_DESKTOP,
        DIRECTIONS_RESULT_VIEW_ID_MOBILE,
      ]);
      app.bus.send('navigation/show', { reset: false });
      app.bus.send('deepLinking/notifyState', {
        id: DIRECTIONS_RESULT_CONTROLS_ID,
        widgetState: undefined,
      });
    };

    const onBackToSearchButtonClicked = () => {
      app.bus.send('navigation/editStops');
      app.bus.send('layers/hideMultiple', [DIRECTIONS_RESULT_VIEW_ID_DESKTOP, DIRECTIONS_RESULT_VIEW_ID_MOBILE]);
      app.bus.send('navigation/show', { reset: false });
    };

    // Announce the "no matches found" feedback to screen readers.

    const getPlacesNearbyForCurrentInput = state => state.searchInputs?.[state.currentInputIndex]?.placesNearby;

    const announceNoResultsIfStillEmpty = () => {
      const placesNearby = getPlacesNearbyForCurrentInput(widgetState.getState());
      if (!(Array.isArray(placesNearby) && placesNearby.length === 0)) {
        return;
      }
      const T = app.gt();
      app.bus.send('ariaAnnouncer/announce', {
        announcementKey: 'directionsSearchResults',
        announcementText: `${T('getDirectionsFromTo:Sorry, no matches were found')} ${T('getDirectionsFromTo:Try searching a different phrase')}`,
      });
    };
    // Debounce the "no matches" announcement so it lands during a genuine typing pause
    const announceNoResultsDebounced = debounce(ANNOUNCE_DEBOUNCE_TIME, announceNoResultsIfStillEmpty);

    const clearNoResultsAnnouncement = () => {
      app.bus.send('ariaAnnouncer/announce', {
        announcementKey: 'directionsSearchResults',
        announcementText: '',
      });
    };

    // Fire only when the current input's `placesNearby` actually changes.
    const announceNoResultsOnPlacesNearbyChange = (state, prevState) => {
      const placesNearby = getPlacesNearbyForCurrentInput(state);
      const prevPlacesNearby = getPlacesNearbyForCurrentInput(prevState);
      if (placesNearby === prevPlacesNearby) {
        return;
      }
      clearNoResultsAnnouncement();
      if (Array.isArray(placesNearby) && placesNearby.length === 0) {
        announceNoResultsDebounced();
      }
    };

    widgetState.addCallback(announceNoResultsOnPlacesNearbyChange);

    async function performSearch(term, isSearchConfirmed = false) {
      // Reset the current input's results synchronously (before the async fetch) so the UI
      // shows the default/loading state rather than a stale "no matches" message in-flight.
      // This placesNearby transition also drives the announcement clear via the callback above.
      {
        const { searchInputs, currentInputIndex } = widgetState.getState();
        const clearedSearchInputs = [...searchInputs];
        clearedSearchInputs[currentInputIndex] = {
          ...clearedSearchInputs[currentInputIndex],
          placesNearby: undefined,
          pois: [],
        };
        widgetState.update({ searchInputs: clearedSearchInputs });
      }

      let searchResults = { placesNearby: undefined, pois: [] };
      if (term) {
        const previousLocation = getPreviousStop();
        const currentLocation = await getLocation(app.bus);
        const nextLocation = getNextStop();

        const poisByTerm = await getPoisByTerm(app.bus, term, previousLocation, nextLocation, currentLocation);
        const navigablePois = poisByTerm.filter(poi => poi.isNavigable);
        const pois = await getPoisWithTransitTimes(
          app.bus,
          navigablePois,
          previousLocation,
          nextLocation,
          currentLocation,
        );
        const placesNearby = pois.slice(0, MAX_UI_RESULTS_LENGTH);
        searchResults = { placesNearby, pois };
        setSearchSideEffects(pois);

        if (isSearchConfirmed) {
          adjustBoundsToPois(pois, app);
        }
      } else {
        setSearchSideEffects(null);
      }

      const { searchInputs, currentInputIndex } = widgetState.getState();
      const updatedSearchInputs = [...searchInputs];
      updatedSearchInputs[currentInputIndex] = {
        ...updatedSearchInputs[currentInputIndex],
        ...searchResults,
        isSearchConfirmed,
      };
      widgetState.update({ searchInputs: updatedSearchInputs });
    }

    // When user moves the map, re-sort the current search results, if any
    app.bus.on('map/moveEnd', async () => {
      const { searchInputs, currentInputIndex } = widgetState.getState();
      if (searchInputs[currentInputIndex]?.pois) {
        const updatedInputs = [...searchInputs];
        const pois = updatedInputs[currentInputIndex].pois;
        const previousLocation = await getLocation(app.bus);
        const sortedPois = await getPoisWithTransitTimes(app.bus, pois, previousLocation);
        updatedInputs[currentInputIndex].pois = sortedPois;
        updatedInputs[currentInputIndex].placesNearby = sortedPois.slice(0, MAX_UI_RESULTS_LENGTH);
        widgetState.update({ searchInputs: updatedInputs });
      }
    });

    const setSearchSideEffects = pois => {
      if (R.length(pois)) {
        app.bus.send('map/selectEntities', { ids: R.pluck('poiId', pois) }); // display map markers
        app.bus.send('searchResults/showPOIs', { pois });
      } else {
        app.bus.send('map/cleanMap');
        app.bus.send('searchResults/showPOIs', { pois: [] });
      }
    };

    const getPreviousStop = () => {
      const { searchInputs, currentInputIndex } = widgetState.getState();
      const previousIndex = currentInputIndex - 1;
      return previousIndex >= 0 && searchInputs[previousIndex]?.chosenPoi
        ? searchInputs[previousIndex].chosenPoi
        : null;
    };

    function getNextStop() {
      const { searchInputs, currentInputIndex } = widgetState.getState();
      const nextIndex = currentInputIndex + 1;
      return nextIndex < searchInputs.length && searchInputs[nextIndex]?.chosenPoi
        ? searchInputs[nextIndex].chosenPoi
        : null;
    }

    const notifyState = () => {
      const { searchInputs, routeAccessibiltyChoice } = widgetState.getState();
      const inputs = Array.isArray(searchInputs) ? searchInputs : [];
      const sendState = {
        id: 'online/getDirectionsFromTo',
        accessible: routeAccessibiltyChoice === RouteType.ACCESSIBLE,
      };
      // if we have multipoint and > 2 endpoints
      if (inputs.length > 2 && allEndPointsDefined(inputs.slice(1))) {
        if (inputs[0] === null || inputs[0] === undefined || inputs[0]?.location?.title === 'My Location') {
          // no start poi or bluedot type poi means showNav
          const endpoints = allSearchEndpoints(inputs.slice(1));
          sendState.waypoints = endpoints;
          sendState.showNav = 'true';
        } else {
          const endpoints = allSearchEndpoints(inputs);
          sendState.waypoints = endpoints;
        }
      } else if (inputs && inputs[1] && inputs[1].chosenPoi) {
        // if we have a destination POI
        if (inputs[0] && inputs[0].chosenPoi) {
          // and a starting POI
          sendState.navFrom = inputs[0].chosenPoi.poiId;
          sendState.navTo = inputs[1].chosenPoi.poiId;
        } else {
          // no start POI
          sendState.showNav = 'true';
          sendState.poiId = inputs[1].chosenPoi.poiId;
        }
      } else {
        // if no POI endpoints defined, no state needed (so clear any existing state)
        delete sendState.accessible;
      }
      app.bus.send('deepLinking/notifyState', sendState);
    };
    widgetState.addCallback(notifyState);

    let oldProm = Zousan.resolve();
    const showMarkersIfNeeded = state => {
      oldProm = oldProm.then(() => showMarkersAsync(state));
    };
    const arrayChange = changeTestForArray();
    const visibilityChange = changeTest({});
    const currentInputIndexChange = changeTest({});

    const showMarkersAsync = async state => {
      const isVisible = await isGetDirectionsVisible();
      const visibilityChanged = visibilityChange(isVisible);
      const inputIndexChanged = currentInputIndexChange(state.currentInputIndex);

      if (!isVisible) {
        return Promise.all([hideStartMarker(), hideAllEndMarkers()]);
      }

      const inputs = Array.isArray(state.searchInputs) ? state.searchInputs : [];
      const rawEndpoints = inputs.map(input => poiOrLocation(input));
      const endpointsChanged = arrayChange(rawEndpoints);
      if (!endpointsChanged && !visibilityChanged && !inputIndexChanged) {
        return;
      }

      const resolvedEndpoints = await Promise.all(
        rawEndpoints.map(ep => ep && app.bus.get('wayfinder/getNavigationEndpoint', { ep })),
      );

      // animate the map only if the set of endpoints is incomplete
      const endpointsAreComplete = R.all(e => !!e)(resolvedEndpoints);
      const markerPromises = resolvedEndpoints.map((endpoint, index) => {
        if (index === 0) {
          if (!endpoint) {
            // oxlint-disable-next-line unicorn/no-single-promise-in-promise-methods -- hideStartMarker() is synchronous (not a Promise); wrap keeps this branch's return type consistent with the sibling branches below
            return Promise.all([hideStartMarker()]);
          }

          return Promise.all([
            addStartMarker(endpoint),
            !endpointsAreComplete && animateToPoint(endpoint.lat, endpoint.lng, endpoint.floorId),
          ]);
        } else {
          if (!endpoint) {
            // oxlint-disable-next-line unicorn/no-single-promise-in-promise-methods -- hideEndMarker() is synchronous (not a Promise); wrap keeps this branch's return type consistent with the sibling branches below
            return Promise.all([hideEndMarker(index)]);
          }
          return Promise.all([
            addEndMarker(endpoint, index),
            !endpointsAreComplete && animateToPoint(endpoint.lat, endpoint.lng, endpoint.floorId),
          ]);
        }
      });

      return Promise.all(markerPromises).then(() => {
        if (resolvedEndpoints.every(ep => !ep)) {
          return Zousan.all([hideStartMarker(), hideAllEndMarkers()]);
        }
      });
    };

    const widgets = [
      DIRECTIONS_RESULT_VIEW_ID_DESKTOP,
      DIRECTIONS_RESULT_VIEW_ID_MOBILE,
      DIRECTIONS_RESULT_CONTROLS_ID,
      DIRECTIONS_SEARCH_CONTROLS_ID,
      DIRECTIONS_SEARCH_VIEW_ID,
    ];
    const isGetDirectionsVisible = () => Zousan.all(widgets.map(id => isLayerShowing(id))).then(R.any(R.equals(true)));

    widgetState.addCallback(showMarkersIfNeeded);

    const showNavigation = async ({ reset = true, referrer }) => {
      // By default reset the state when showing this plugin
      if (reset) {
        widgetState.update(getDefaultState(widgetState));
        await resetInputData({
          index: FROM_SEARCH_FIELD_ID,
          usePhysicalLocation: true,
        });
        if (Object.keys(widgetState.getState().searchInputs[FROM_SEARCH_FIELD_ID]).length) {
          // if first search input has any data (e.g. from bluedot) switch active input to TO
          widgetState.update({ currentInputIndex: TO_SEARCH_FIELD_ID });
        }
      }
      if (referrer) {
        widgetState.update({ referrer });
      } // used in analytics

      const isDesktop = app.env.isDesktop();

      resetUnavailableRouteInfo();
      const { defaultSearch } = widgetState.getState();
      if (!defaultSearch.length) {
        await app.bus
          .get('search/getDefaultSearchPois', { limit: 6 })
          .then(defaultSearch => widgetState.update({ defaultSearch }));
      }
      app.bus.send('map/resetNavlineFeatures');
      app.bus.send('layers/show', { id: DIRECTIONS_SEARCH_CONTROLS_ID });
      app.bus.send('layers/show', { id: DIRECTIONS_SEARCH_VIEW_ID });
      app.bus.send('history/register', {
        viewId: isDesktop ? DIRECTIONS_RESULT_VIEW_ID_DESKTOP : DIRECTIONS_RESULT_VIEW_ID_MOBILE,
        event: 'navigation/show',
        params: { reset },
      });
    };

    app.bus.on('navigation/show', showNavigation);
    app.bus.on('online/handleSearchInputChange', ({ term }) => {
      handleInputValueChange(term);
    });

    app.bus.on('online/setCurrentSearchInput', ({ id }) => {
      setCurrentInputIndex(id); // But this is really an index
    });

    app.bus.on('online/resetSearchInputData', ({ index, usePhysicalLocation }) => {
      resetInputData({ index, usePhysicalLocation });
    });

    const addStartMarker = async endpoint => {
      // If app.bus.on('user/physicalLocation') has returned a pinnedLocation, don't add the normal start marker
      if (endpoint.isPinned) {
        return null;
      }
      const markerComponent = <Icon name="wayfinding.start" cdn width={22} height={54} />;
      const { floorName, structureName } = await getMarkerStructurePosition(endpoint);
      const T = app.gt();
      const text = T('getDirectionsFromTo:Start at _structureName_ on _floorName_', { structureName, floorName });
      const ghostMarkerComponent = (
        <GhostMarker>
          <Icon name="wayfinding.start.ghost" cdn width={22} height={54} />
          <span>{text}</span>
        </GhostMarker>
      );
      app.bus.send('map/addGhostedMarker', {
        id: ROUTE_START_MARKER_ID,
        ordinal: endpoint.ordinal,
        lat: endpoint.lat,
        lng: endpoint.lng,
        markerComponent,
        ghostMarkerComponent,
        ghostMarkerOptions: { anchor: 'bottom-left', offset: [-17, 0] },
        ariaLabel: T('getDirectionsFromTo:Route start marker'),
      });
    };

    const addEndMarker = async (endpoint, index) => {
      const markerComponent = <Icon name="wayfinding.end" cdn width={34} height={47} />;
      const { floorName, structureName } = await getMarkerStructurePosition(endpoint);
      const T = app.gt();
      const text = T('getDirectionsFromTo:Arrive at _structureName_ on _floorName_', {
        structureName,
        floorName,
      });
      const ghostMarkerComponent = (
        <GhostMarker>
          <Icon name="wayfinding.end.ghost" cdn width={34} height={47} />
          <span>{text}</span>
        </GhostMarker>
      );
      const markerId = getRouteEndMarkerId(index);
      app.bus.send('map/addGhostedMarker', {
        id: markerId,
        ordinal: endpoint.ordinal,
        lat: endpoint.lat,
        lng: endpoint.lng,
        markerComponent,
        ghostMarkerComponent,
        ghostMarkerOptions: { anchor: 'bottom-left', offset: [-25, 0] },
        ariaLabel: T('getDirectionsFromTo:Route end marker'),
      });
    };

    const getMarkerStructurePosition = async endpoint => {
      const { lat, lng, floorId, ordinal: originalOrdinal } = endpoint;
      const translatedFloorId = await app.bus.get('venueData/getTranslatedFloorId', { floorId });
      const { floorName, structureName } = await app.bus.get('venueData/getFloorIdName', {
        floorId: translatedFloorId,
      });
      const ordinalFilter = ordinal => ordinal !== originalOrdinal;

      return { lat, lng, floorName, structureName, ordinalFilter };
    };

    const hideStartMarker = () => app.bus.send('map/removeMarker', { id: ROUTE_START_MARKER_ID });

    const hideAllEndMarkers = () => {
      for (let i = 1; i < MAX_SEARCH_INPUT_LENGTH; i++) {
        // start from 1, because the first is the start marker
        app.bus.send('map/removeMarker', { id: getRouteEndMarkerId(i) });
      }
    };

    const hideEndMarker = index => {
      const markerId = getRouteEndMarkerId(index);
      app.bus.send('map/removeMarker', { id: markerId });
    };

    // HEADER
    app.bus.on('venueData/mapDataLoaded', ({ structures: buildings }) => {
      widgetState.update({ buildings });
    });

    const setLevelPopupTimeout = newLevelPopupTimeout => {
      if (levelPopupTimeout) {
        clearTimeout(levelPopupTimeout);
      }
      widgetState.update({ showLevelName: false });
      levelPopupTimeout = newLevelPopupTimeout;
    };

    app.bus.on('map/floorChanged', ({ floor }) => {
      const { isNavigating, currentLevelName } = widgetState.getState();

      if (!isNavigating) {
        return;
      }

      if (!floor) {
        return widgetState.update({ currentLevelName: null });
      }

      const newLevelName = floor.name;

      if (currentLevelName !== newLevelName) {
        const newLevelPopupTimeout = setTimeout(
          () => {
            widgetState.update({ showLevelName: false });
          },
          (ANIMATION_TIME + ANIMATION_DELAY) * 1000,
        );

        setLevelPopupTimeout(newLevelPopupTimeout);

        widgetState.update({
          currentLevelName: newLevelName,
          showLevelName: true,
        });
      }
    });

    const setCurrentInputIndex = index => {
      const { searchInputs, currentInputIndex } = widgetState.getState();
      showSearchView();

      if (currentInputIndex !== index) {
        // take action only if focused other input than previously
        widgetState.update({ currentInputIndex: index });
        const pois = searchInputs[index]?.pois || [];
        setSearchSideEffects(pois);
      }
    };

    const checkAndSetStartingLocation = async () => {
      // Set the first input to physical location if available
      const location = await app.bus.getFirst('user/getPhysicalLocation');
      if (location) {
        if (!location.floorId) {
          const floor = await app.bus.get('map/getFloorAt', location);
          location.floorId = floor ? floor.id : null;
        }
        const { searchInputs } = widgetState.getState();
        const updatedSearchInputs = [...searchInputs];
        updatedSearchInputs[0] = { term: location.title, location };
        widgetState.update({ searchInputs: updatedSearchInputs });
      }
    };

    const resetSearchInputs = async () => {
      // First clear the state to defaults
      widgetState.update({
        endpointIds: [],
        endpoints: [],
        directRoutes: [],
        accessibleRoutes: [],
        searchInputs: [{}, {}],
      });
      // Then set the first input to physical location if available
      await checkAndSetStartingLocation();
    };

    const closeDirections = async () => {
      app.bus.send('headerOnline/show');
      app.bus.send('search/customAction', { name: null, category: null });
      app.bus.send('layers/hideMultiple', [DIRECTIONS_RESULT_VIEW_ID_DESKTOP, DIRECTIONS_RESULT_VIEW_ID_MOBILE]);
      app.bus.send('layers/hideMultiple', [
        DIRECTIONS_RESULT_VIEW_ID_DESKTOP,
        DIRECTIONS_RESULT_VIEW_ID_MOBILE,
        DIRECTIONS_RESULT_CONTROLS_ID,
        DIRECTIONS_SEARCH_CONTROLS_ID,
        DIRECTIONS_SEARCH_VIEW_ID,
        LANE_PICKER_VIEW_ID,
      ]);
      widgetState.update(getDefaultState(widgetState));
      app.bus.send('deepLinking/notifyState', {
        id: DIRECTIONS_SEARCH_VIEW_ID,
        widgetState: undefined,
      });

      checkAndSetStartingLocation();
      app.bus.send('map/cleanMap');
      app.bus.send('searchResults/showPOIs', { pois: [] });
    };

    const exitGetDirections = () => app.bus.send('navigation/closeDirections', { reason: 'closed' });

    app.bus.on('navigation/closeDirections', ({ reason }) => {
      if (reason === 'closed') {
        return closeDirections();
      }
    });
    app.bus.on('app/reset', closeDirections);

    const setInputData = data => {
      const { searchInputs, currentInputIndex } = widgetState.getState();
      const updatedSearchInputs = [...searchInputs];
      updatedSearchInputs[currentInputIndex] = {
        ...updatedSearchInputs[currentInputIndex],
        ...data,
      };
      updatedSearchInputs[currentInputIndex].chosenPoi = null;
      widgetState.update({ searchInputs: [...updatedSearchInputs] });
    };

    const setInputChosenPoi = chosenPoi => {
      const { searchInputs, currentInputIndex } = widgetState.getState();

      const updatedSearchInputs = [...searchInputs];
      updatedSearchInputs[currentInputIndex] = {
        ...updatedSearchInputs[currentInputIndex],
        chosenPoi,
      };
      updatedSearchInputs[currentInputIndex].term = chosenPoi.name;
      widgetState.update({
        searchInputs: [...updatedSearchInputs],
        currentInputIndex: getNextEmptySearchFieldIndex(updatedSearchInputs, currentInputIndex),
      });
      showSearchView();
      app.bus.send('map/cleanMap');
    };

    // debounce some stuff to improve UX
    const getSuggestionsDebounced = debounce(DEBOUNCE_TIME, performSearch);
    const setInputDataDebounced = debounce(DEBOUNCE_TIME, setInputData);

    function handleInputValueChange(term) {
      resetUnavailableRouteInfo();
      setInputDataDebounced({ term });
      getSuggestionsDebounced(term);
    }

    const showSearchView = () => app.bus.send('layers/show', { id: DIRECTIONS_SEARCH_VIEW_ID });
    const hideSearchView = () => app.bus.send('layers/hide', { id: DIRECTIONS_SEARCH_VIEW_ID });

    // Updates the physical location of the user. If we are showing navigation and the starting point
    // is the user's location, update the navigation to reflect the user's new position
    async function updatePhysicalLocation(latitude, longitude, floorId) {
      const isDesktop = app.env.isDesktop();
      // note: floorId may be null/undefined
      const isNavigationShowing = await isLayerShowing(
        isDesktop ? DIRECTIONS_RESULT_VIEW_ID_DESKTOP : DIRECTIONS_RESULT_VIEW_ID_MOBILE,
      );
      if (isNavigationShowing && floorId) {
        const { searchInputs, routeAccessibiltyChoice } = widgetState.getState();
        if (searchInputs && searchInputs[FROM_SEARCH_FIELD_ID] && searchInputs[FROM_SEARCH_FIELD_ID].location) {
          searchInputs[FROM_SEARCH_FIELD_ID] = {
            ...searchInputs[FROM_SEARCH_FIELD_ID],
            location: {
              title: searchInputs[FROM_SEARCH_FIELD_ID].term,
              latitude,
              longitude,
              floorId,
            },
          };
          widgetState.update({ searchInputs });
          if (allEndPointsDefined(searchInputs)) {
            app.bus.send('navigation/showDirectionsFromTo', {
              endpointIds: allSearchEndpoints(searchInputs),
              requiresAccessibility: routeAccessibiltyChoice,
              doNotMoveMap: true,
            });
          }
        }
      }
    }

    const MIN_RENAV_METERS = 5; // minimum number of meters the user's physical location should change before we reroute navigation
    const updatePhysicalLocationThrottled = throttle(updatePhysicalLocation, 20); // every 2 seconds is plenty fast
    const maybeUpdatePhysicalLocation = (() => {
      let lastUpdate = {};
      return (lat, lng, floorId) => {
        // floorId may be null/undefined
        if (
          !lastUpdate ||
          floorId !== lastUpdate.floorId ||
          distance(lat, lng, lastUpdate.lat, lastUpdate.lng) > MIN_RENAV_METERS
        ) {
          updatePhysicalLocationThrottled(lat, lng, floorId);
          lastUpdate = { lat, lng, floorId };
        }
      };
    })();

    app.bus.on(
      'bluedot/locationUpdate',
      (
        { latitude, longitude, floorId }, // Note: floorId may be null/undefined
      ) => maybeUpdatePhysicalLocation(latitude, longitude, floorId),
    );

    const searchDirectionsToId = async (poiId, waypoints) => {
      let inputData = {};
      let currentInputIndex = FROM_SEARCH_FIELD_ID;

      await resetInputData({
        index: FROM_SEARCH_FIELD_ID,
        usePhysicalLocation: true,
      });

      const { searchInputs } = widgetState.getState();
      let updatedSearchInputs;
      if (waypoints) {
        try {
          const navEndpoints = await Promise.all(waypoints.map(async id => await getInputNewData(id)));
          updatedSearchInputs = [searchInputs[0], ...navEndpoints];
        } catch {
          updatedSearchInputs = [...searchInputs];
        }
      } else {
        const poi = await app.bus.get('poi/getById', { id: poiId }).catch(error => console.error(error));

        if (poi && poi.poiId) {
          // if the poi object was sent update input with real poi data
          inputData = { chosenPoi: poi, term: poi.name };
        } else {
          inputData = { chosenPoi: null, term: null };
          currentInputIndex = TO_SEARCH_FIELD_ID;
        }

        updatedSearchInputs = [...searchInputs];
        updatedSearchInputs[TO_SEARCH_FIELD_ID] = {
          ...updatedSearchInputs[TO_SEARCH_FIELD_ID],
          ...inputData,
        };
      }
      return { updatedSearchInputs, currentInputIndex };
    };

    const addNewPOIStop = async (poiId, searchInputs) => {
      const poi = await app.bus.get('poi/getById', { id: poiId }).catch(error => console.error(error));

      if (poi && poi.poiId) {
        // if the poi object was sent update input with real poi data
        // if last input is blank then overwrite it
        if (R.isEmpty(searchInputs[searchInputs.length - 1])) {
          return [...searchInputs.slice(0, -1), { chosenPoi: poi, term: poi.name }];
        } else if (searchInputs.length < 7) {
          return [...searchInputs, { chosenPoi: poi, term: poi.name }];
        }
      } else {
        return searchInputs;
      }
    };

    app.bus.on('navigation/searchDirectionsToId', async ({ poiId, waypoints, referrer, keepExistingStops }) => {
      const { searchInputs } = widgetState.getState();
      let { currentInputIndex } = widgetState.getState();
      let updatedSearchInputs;

      if (searchInputs.length === 7) {
        return;
      }

      if (keepExistingStops) {
        updatedSearchInputs = await addNewPOIStop(poiId, searchInputs);
        currentInputIndex = getNextEmptySearchFieldIndex(updatedSearchInputs, currentInputIndex);
      } else {
        ({ updatedSearchInputs, currentInputIndex } = await searchDirectionsToId(poiId, waypoints));
      }

      widgetState.update({
        searchInputs: updatedSearchInputs,
        currentInputIndex,
        referrer,
      });

      app.bus.send('navigation/show', { reset: false });
    });

    app.bus.on('navigation/searchDirectionsFromId', async ({ poiId, referrer }) => {
      resetInputData({
        index: FROM_SEARCH_FIELD_ID,
        usePhysicalLocation: false,
      });
      resetInputData({ index: TO_SEARCH_FIELD_ID });

      const poi = await app.bus.get('poi/getById', { id: poiId }).catch(error => console.error(error));
      const inputData = {
        chosenPoi: poi,
        term: R.prop('name', poi),
      };

      const { searchInputs } = widgetState.getState();
      const newInputs = [...searchInputs];
      newInputs[FROM_SEARCH_FIELD_ID] = R.mergeRight(searchInputs[FROM_SEARCH_FIELD_ID], inputData);
      // newInputs[FROM_SEARCH_FIELD_ID] = {...searchInputs[FROM_SEARCH_FIELD_ID], ...inputData}
      widgetState.update({
        searchInputs: newInputs,
        currentInputIndex: TO_SEARCH_FIELD_ID,
        referrer,
      });
      app.bus.send('navigation/show', { reset: false });
    });

    // The id param below is actual the index of the input field
    const resetInputData = async ({ index = null, usePhysicalLocation = false }) => {
      const { searchInputs, currentInputIndex } = widgetState.getState();
      const inputIndex = index !== null ? index : currentInputIndex;
      const updatedSearchInputs = [...searchInputs];
      updatedSearchInputs[inputIndex] = {};
      if (usePhysicalLocation) {
        // if there is physical location available set current input with it
        const location = await app.bus.getFirst('user/getPhysicalLocation');
        if (location) {
          if (!location.floorId) {
            const floor = await app.bus.get('map/getFloorAt', location); // if we don't have a floorId, use current level
            location.floorId = floor ? floor.id : null; // TODO: this isn't right!
          }
          updatedSearchInputs[inputIndex] = { term: location.title, location };
        }
      }

      widgetState.update({
        searchInputs: updatedSearchInputs,
        currentInputIndex: inputIndex,
      });

      app.bus.send('map/cleanMap');
      app.bus.send('searchResults/showPOIs', { pois: [] });
    };

    app.bus.on('navigation/getSearchInputsLength', () => {
      const { searchInputs } = widgetState.getState();
      return definedEndpointCount(searchInputs);
    });

    app.bus.on('map/poiClicked', async ({ poi }) => {
      const isNavigationSearchView = await isLayerShowing('DirectionsSearchControls');
      if (isNavigationSearchView && poi.isNavigable) {
        setInputChosenPoi(poi);
      }
    });

    let lastEndpointIds = [];
    let lastAllEndpointsDefined = false;

    function callShowDirectionsIfEndpointsDefined(state, previousState) {
      const {
        searchInputs,
        endpointIds: stateEndpointIds,
        selectedSecurityLanes,
        isRoutePending,
        hasLoaded,
      } = widgetState.getState();
      const currentlyAllDefined = allEndPointsDefined(searchInputs);

      if (!currentlyAllDefined) {
        lastAllEndpointsDefined = false;
        return;
      }

      const newEndpointIds = allSearchEndpoints(searchInputs);

      // Force re-display if we're coming from an undefined state (e.g., after adding/removing a stop)
      const wasUndefined = !lastAllEndpointsDefined;
      lastAllEndpointsDefined = true;

      // Helper to compare endpoint IDs (handles both POI IDs and location objects)
      const endpointIdsEqual = (ids1, ids2) => {
        if (!ids1 || !ids2 || ids1.length !== ids2.length) {
          return false;
        }
        return ids1.every((epId, index) => {
          const otherId = ids2[index];
          // Handle location objects
          if (typeof epId === 'object' && typeof otherId === 'object') {
            return epId.latitude === otherId.latitude && epId.longitude === otherId.longitude;
          }
          return epId === otherId;
        });
      };

      // Compare with both lastEndpointIds (closure) and stateEndpointIds (widget state)
      // This prevents re-triggering when:
      // 1. We've already called showDirectionsFromTo with these endpoints (lastEndpointIds match)
      // 2. Routes are already being displayed for these endpoints (stateEndpointIds match)
      const matchesLastEndpoints = endpointIdsEqual(newEndpointIds, lastEndpointIds);
      const matchesStateEndpoints = endpointIdsEqual(newEndpointIds, stateEndpointIds);

      // Resolving a requested route also hydrates its inputs; that update must not start a second request.
      if (matchesStateEndpoints && isRoutePending && !hasLoaded) {
        lastEndpointIds = newEndpointIds;
        return;
      }

      const editedInvalidRoute = isRoutePending && hasLoaded && state.searchInputs !== previousState.searchInputs;
      if ((matchesLastEndpoints || matchesStateEndpoints) && !wasUndefined && !editedInvalidRoute) {
        return;
      }

      app.bus.send('navigation/showDirectionsFromTo', {
        endpointIds: newEndpointIds,
        selectedSecurityLanes,
      });
      lastEndpointIds = newEndpointIds;
    }

    widgetState.addCallback(callShowDirectionsIfEndpointsDefined);

    const runTest = async (initialState, testRoutine) => {
      widgetState.update(getDefaultState(widgetState));
      widgetState.update(initialState);
      await testRoutine();
      const resultState = widgetState.getState();
      return resultState;
    };

    return {
      init,
      getState: () => widgetState.getState(),
      runTest,
      internal: {
        ...showDirectionsFromToTestMethods,
        announceNoResultsOnPlacesNearbyChange,
        displayRoutes,
        addStartMarker,
        getNextStop,
        getPreviousStop,
        handleInputValueChange,
        hideSearchView,
        notifyState,
        onResultControlBackButtonClicked: exitGetDirections,
        performSearch,
        resetInputData,
        resetSearchInputs,
        resetUnavailableRouteInfo,
        setCurrentInputIndex,
        setInputChosenPoi,
        setInputData,
        showMarkersAsync,
        showSearchView,
        switchDirections,
        switchRouteType,
        toggleIsNavigating,
        updatePhysicalLocation,
      },
    };
  };

const create = (app, ...args) => {
  const state = new StateObjMultiple();

  const createFn = _create(state)(app, ...args);

  return createFn;
};
export { create, _create };
